import React from "react";
import {
  Box,
  Toolbar,
  Divider,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Drawer,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import { useIntl } from "react-intl";
import VolunteerActivismIcon from "@mui/icons-material/VolunteerActivism";
import DesignTreeMember from "../tree/DesignTreeMember";
import StrategyTreeMember from "../tree/StrategyTreeMember";
import HomeIcon from "@mui/icons-material/Home";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import EmojiObjectsIcon from "@mui/icons-material/EmojiObjects";
import PsychologyIcon from "@mui/icons-material/Psychology";

export const SIDEBAR_WIDTH = 260;

const activeStyle = {
  backgroundColor: "rgba(46, 125, 50, 0.08)",
  color: "#2E7D32",
  fontWeight: 700,
  borderLeft: "3px solid #2E7D32",
};

const BOTTOM_ITEMS = [
  { key: "workers", labelKey: "menu.workers", icon: FormatListBulletedIcon, route: "/workers" },
  { key: "ideas", labelKey: "menu.ideas", icon: EmojiObjectsIcon, route: "/ideas" },
  { key: "awareness", labelKey: "menu.awareness", icon: PsychologyIcon, route: "/awareness" },
];

function SidebarContent({ onClose }) {
  const navigate = useNavigate();
  const location = useLocation();
  const intl = useIntl();
  const t = (id) => intl.formatMessage({ id });

  const isActive = (route, exact) => {
    if (exact) return location.pathname === route;
    return location.pathname.startsWith(route);
  };

  const handleNavigate = (path) => {
    navigate(path);
    if (onClose) onClose();
  };

  return (
    <Box sx={{ p: 1 }}>
      {/* ホーム */}
      <List dense disablePadding>
        <ListItemButton
          sx={{ borderRadius: 1, mb: 0.25, ...(isActive("/", true) ? activeStyle : {}) }}
          onClick={() => handleNavigate("/")}
        >
          <ListItemIcon sx={{ minWidth: 36 }}>
            <HomeIcon fontSize="small" color={isActive("/", true) ? "inherit" : "action"} />
          </ListItemIcon>
          <ListItemText
            primary={t("menu.home")}
            primaryTypographyProps={{ fontWeight: isActive("/", true) ? 700 : 400, fontSize: "0.875rem" }}
          />
        </ListItemButton>
      </List>

      <Divider sx={{ my: 0.5 }} />

      {/* 経営理念 */}
      <List dense disablePadding>
        <ListItemButton
          sx={{ borderRadius: 1, mb: 0.25, ...(isActive("/company-values", true) ? activeStyle : {}) }}
          onClick={() => handleNavigate("/company-values")}
        >
          <ListItemIcon sx={{ minWidth: 36 }}>
            <VolunteerActivismIcon fontSize="small" color={isActive("/company-values", true) ? "inherit" : "action"} />
          </ListItemIcon>
          <ListItemText
            primary={t("tree.companyValues")}
            primaryTypographyProps={{ fontWeight: isActive("/company-values", true) ? 700 : 400, fontSize: "0.875rem" }}
          />
        </ListItemButton>
      </List>

      {/* 設計ツリー */}
      <DesignTreeMember onNavigate={onClose} />

      {/* 戦略ツリー */}
      <StrategyTreeMember onNavigate={onClose} />

      <Divider sx={{ my: 0.5 }} />

      {/* ワーカー・アイデア・気づき */}
      <List dense disablePadding>
        {BOTTOM_ITEMS.map((item) => {
          const Icon = item.icon;
          const active = isActive(item.route, false);
          return (
            <ListItemButton
              key={item.key}
              sx={{ borderRadius: 1, mb: 0.25, ...(active ? activeStyle : {}) }}
              onClick={() => handleNavigate(item.route)}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                <Icon fontSize="small" color={active ? "inherit" : "action"} />
              </ListItemIcon>
              <ListItemText
                primary={t(item.labelKey)}
                primaryTypographyProps={{ fontWeight: active ? 700 : 400, fontSize: "0.875rem" }}
              />
            </ListItemButton>
          );
        })}
      </List>
    </Box>
  );
}

export default function AppSidebar({ mobileOpen, onClose }) {
  const drawerSx = {
    width: SIDEBAR_WIDTH,
    flexShrink: 0,
    "& .MuiDrawer-paper": {
      width: SIDEBAR_WIDTH,
      boxSizing: "border-box",
      borderRight: "1px solid #e0e0e0",
      bgcolor: "#fafafa",
    },
  };

  return (
    <Box component="nav">
      {/* モバイル: temporary Drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onClose}
        ModalProps={{ keepMounted: true }}
        sx={{ display: { xs: "block", md: "none" }, ...drawerSx }}
      >
        <Toolbar />
        <SidebarContent onClose={onClose} />
      </Drawer>

      {/* デスクトップ: permanent Drawer */}
      <Drawer
        variant="permanent"
        open
        sx={{ display: { xs: "none", md: "block" }, ...drawerSx }}
      >
        <Toolbar />
        <SidebarContent />
      </Drawer>
    </Box>
  );
}
